/**
 * TTSSourceSelector — thin wrapper around ProviderPicker.
 *
 * Maps TTS_SOURCE_OPTIONS to PickerOption[], groups by Local vs Cloud via
 * groupBy={(o) => o.meta?.tier}, and delegates layout / sort / collapsed-
 * preview to the shared ProviderPicker primitive.
 *
 * Local providers are marked free via isLocalPricingActive().
 * The local-GPU discount info note is rendered inside renderCardExtras.
 */

import { memo, useMemo } from 'react';
import { Volume2 } from 'lucide-react';
import { useTranslation } from '../../hooks/useTranslation';
import {
  TTS_SOURCE_OPTIONS,
  TTS_SOURCE_CATEGORY_LABELS,
  isAlwaysFreeTtsSource,
} from '../../constants/ttsSources';
import { isServerlessMode } from '../../utils/apiMode';
import { getEffectivePricing, isLocalPricingActive } from '../../utils/localPricing';
import { useLocalTtsInstallStatus } from '../../hooks/useLocalTtsInstallStatus';
import { ProviderPicker } from '../common/ProviderPicker';
import type { PickerOption } from '../common/ProviderPicker';

interface TTSSourceSelectorProps {
  value: string;
  onChange: (provider: string) => void;
  disabled?: boolean;
  className?: string;
}

type TTSMeta = { tier: string; isLocal: boolean };

export const TTSSourceSelector = memo(function TTSSourceSelector({
  value,
  onChange,
  disabled = false,
  className = '',
}: TTSSourceSelectorProps) {
  const { t } = useTranslation();
  const serverless = isServerlessMode();
  // Kokoro and F5-TTS are the GPU TTS options with a real ComponentManager-
  // backed install ("kokoro_tts", utils/components/kokoro_tts_manager.py;
  // "f5_tts", utils/components/f5_tts_manager.py) that the FULL VIDEO PIPELINE
  // cost estimator actually consults: VideoConfigForm.tsx marks exactly these
  // (plus "piper_tts", which has no card here) as locally-available in the
  // `availability` map it sends to PipelineCostEstimator
  // (constants/pricing/pipeline.py _is_local). This selector renders on that
  // SAME screen (inside AudioLanguagesSection) next to FullPipelineCostCard,
  // which reads that estimator — so this card and that one must agree, and the
  // two lists must be changed together.
  //
  // F5-TTS was in the "no ComponentManager, always billed cloud" bucket until
  // it got one. Leaving it there meant a user could install it locally and
  // still be quoted the cloud price for a model sitting on their own disk.
  // Chatterbox still has no ComponentManager, so `installed` stays false for
  // it (RULE #5: no fallback to "capability").
  //
  // Edge TTS and PyTTSx3 are the exception: per product decision they are
  // billed free on EVERY platform (desktop AND Cloud Run) because they never
  // consume Asgard infra — isAlwaysFreeTtsSource() (backed by
  // ALWAYS_FREE_TTS_PROVIDERS in constants/pricing/tts.py) short-circuits
  // the install/desktop gate entirely for those two, matching
  // audio_estimator.py's is_tts_provider_free() and pipeline.py's
  // _LOCAL_ONLY_COMPONENT_IDS (RULE #-2: one source of truth for both
  // screens).
  //
  // `isLocal` on the option keeps its "runs on local GPU/CPU" CATEGORY
  // meaning (still groups everything under "Local (GPU)"); `installed` /
  // `isAlwaysFreeTtsSource` are the separate "confirmed billed at 0 right
  // now" signals.
  // Provider id -> local component installed? Shared with VideoConfigForm and
  // AudioConfigForm so this card cannot disagree with the cost cards beside it.
  const installedBySource: Record<string, boolean> = useLocalTtsInstallStatus();

  const options = useMemo<PickerOption<TTSMeta>[]>(() => {
    return TTS_SOURCE_OPTIONS.map((opt) => {
      const alwaysFree = isAlwaysFreeTtsSource(opt.value);
      const installed = installedBySource[opt.value] === true;
      const effectivePricing = getEffectivePricing(opt.pricing, opt.isLocal, { installed });
      const localActive = alwaysFree || isLocalPricingActive(opt.isLocal, installed);

      // Group label respects serverless mode labels
      const tierLabel =
        opt.category === 'local'
          ? serverless
            ? 'Asgard Cloud'
            : TTS_SOURCE_CATEGORY_LABELS.local
          : TTS_SOURCE_CATEGORY_LABELS.cloud;

      return {
        value: opt.value,
        label: opt.label,
        description: opt.description,
        pricing: localActive ? t('common.free', { defaultValue: 'Free' }) : effectivePricing,
        pricingNumeric: localActive ? 0 : undefined,
        recommended: opt.recommended === true,
        meta: { tier: tierLabel, isLocal: opt.isLocal },
      } satisfies PickerOption<TTSMeta>;
    });
  }, [serverless, t, installedBySource]);

  return (
    <div className={`form-group ${className}`}>
      <label className="form-label flex items-center gap-2 mb-1">
        <Volume2 className="h-4 w-4 flex-shrink-0" />
        <span>{t('ttsSource.title', { defaultValue: 'TTS Provider' })}</span>
      </label>
      <p className="text-xs text-white/50 mb-2">
        {t('ttsSource.description', {
          defaultValue: 'Choose the text-to-speech provider for audio generation',
        })}
      </p>

      <ProviderPicker<TTSMeta>
        options={options}
        value={value}
        onChange={onChange}
        sortKeys={['price', 'speed', 'quality', 'name']}
        defaultSortKey="price"
        disabled={disabled}
        groupBy={(opt) => opt.meta?.tier ?? null}
      />
    </div>
  );
});

export default TTSSourceSelector;
